import { mockStudentStorage } from '../mockStudentStorage';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import { Avatar, Box, Button, ButtonBase, Stack, Typography } from '@mui/material';
import React from 'react';
import { useHistory } from 'react-router-dom';
import PhoneFrame from '../components/PhoneFrame';
import ProgressRing from '../components/ProgressRing';
import {
    completedLessonCount, coursePercent, CourseIconKey, getCurrentAssignment, mockCourses, StudentCourse,
} from '../mockData';
import { getLatestReviewedAttempt } from '../mockFeedback';
import { normalizeStudentAvatarColor } from '../studentAvatarColors';
import { cardSx, GREEN, INTER, NAVY, POPPINS, SLATE, SUCCESS_TEXT, yellowButtonSx } from '../studentStyles';

const courseIcons: Record<CourseIconKey, React.ElementType> = {
    book: MenuBookRoundedIcon,
    chat: ForumRoundedIcon,
    voice: RecordVoiceOverRoundedIcon,
};

function CourseCard({ course }: { course: StudentCourse }) {
    const history = useHistory();
    const CourseIcon = courseIcons[course.icon];
    const completed = completedLessonCount(course);
    const allDone = completed === course.lessons.length;

    return (
        <ButtonBase
            onClick={() => history.push(`/mockup/v2/student/course/${course.id}`)}
            sx={{ ...cardSx, width: '100%', p: 2, display: 'flex', alignItems: 'center', gap: 2, textAlign: 'left' }}
        >
            <Box sx={{
                width: 44, height: 44, borderRadius: '14px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: `${course.accentColor}26`,
            }}>
                <CourseIcon sx={{ color: course.accentColor, fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>
                    {course.name}
                </Typography>
                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: allDone ? SUCCESS_TEXT : SLATE }}>
                    {allDone
                        ? 'All lessons complete'
                        : `${completed} of ${course.lessons.length} lessons complete`}
                </Typography>
            </Box>
            <ProgressRing percent={coursePercent(course)} size={28} thickness={5} />
            <ChevronRightRoundedIcon sx={{ color: SLATE }} />
        </ButtonBase>
    );
}

export default function StudentHome() {
    const history = useHistory();

    const studentName = mockStudentStorage.getItem('studentName') || 'Maya Chen';
    const avatarColor = normalizeStudentAvatarColor(mockStudentStorage.getItem('studentAvatarColor'));
    const current = getCurrentAssignment();
    const reviewedAttempt = getLatestReviewedAttempt();

    return (
        <PhoneFrame>
            <Stack spacing={3}>

                {/* Greeting + avatar */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 26, color: NAVY }}>
                            Hi, {studentName}
                        </Typography>
                        <Typography sx={{ fontFamily: INTER, fontSize: 15, color: SLATE }}>
                            Ready for a little Chinese today?
                        </Typography>
                    </Box>
                    <ButtonBase
                        aria-label="Profile"
                        onClick={() => history.push('/mockup/v2/student/profile')}
                        sx={{ borderRadius: '50%', minWidth: 44, minHeight: 44 }}
                    >
                        <Avatar sx={{ width: 40, height: 40, bgcolor: avatarColor, fontFamily: POPPINS, fontWeight: 600, fontSize: 16 }}>
                            {studentName.charAt(0).toUpperCase()}
                        </Avatar>
                    </ButtonBase>
                </Box>

                {/* Hero: current assignment */}
                {current ? (
                    <Box sx={{ ...cardSx, p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 11, letterSpacing: '0.08em', color: SLATE, mb: 0.75 }}>
                                    {current.lesson.completedPrompts === 0 ? 'YOUR NEXT LESSON' : 'CONTINUE WHERE YOU LEFT OFF'}
                                </Typography>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY, lineHeight: 1.3 }}>
                                    {current.lesson.name}
                                </Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 14, color: SLATE, mt: 0.5 }}>
                                    {current.course.name}
                                </Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 1 }}>
                                    {current.lesson.completedPrompts} of {current.lesson.totalPrompts} exercises done
                                </Typography>
                            </Box>
                            <ProgressRing percent={current.percent} size={96} thickness={4} showLabel />
                        </Box>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<PlayArrowRoundedIcon />}
                            onClick={() => history.push(`/mockup/v2/student/course/${current.course.id}/lesson/${current.lesson.id}`)}
                            sx={{ ...yellowButtonSx, mt: 2.5 }}
                        >
                            {current.lesson.completedPrompts === 0 ? 'Start lesson' : 'Continue'}
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: SUCCESS_TEXT }}>
                            You&apos;re all caught up
                        </Typography>
                        <Typography sx={{ fontFamily: INTER, fontSize: 14, color: SLATE, mt: 0.5 }}>
                            Nice work — check back for your next assignment.
                        </Typography>
                    </Box>
                )}

                {/* Teacher feedback */}
                {reviewedAttempt?.review && (
                    <ButtonBase
                        onClick={() => history.push('/mockup/v2/student/feedback')}
                        sx={{ ...cardSx, width: '100%', p: 2, display: 'flex', alignItems: 'center', gap: 2, textAlign: 'left' }}
                    >
                        <Box sx={{
                            width: 44, height: 44, borderRadius: '14px', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: `${GREEN}1A`,
                        }}>
                            <RateReviewRoundedIcon sx={{ color: GREEN, fontSize: 24 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 11, letterSpacing: '0.08em', color: SLATE }}>
                                TEACHER FEEDBACK
                            </Typography>
                            <Typography noWrap sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15, color: NAVY, mt: 0.25 }}>
                                {reviewedAttempt.sentenceHanzi}
                            </Typography>
                            <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SUCCESS_TEXT }}>
                                Reviewed by your teacher · {reviewedAttempt.review.teacherScore}%
                            </Typography>
                        </Box>
                        <ChevronRightRoundedIcon sx={{ color: SLATE }} />
                    </ButtonBase>
                )}

                {/* Course list */}
                <Button onClick={() => history.push('/mockup/v2/student/pronunciation')} startIcon={<RecordVoiceOverRoundedIcon />} endIcon={<ChevronRightRoundedIcon />} sx={{ ...cardSx, p: 2, justifyContent: 'flex-start', fontFamily: INTER, fontWeight: 700, color: NAVY, textTransform: 'none' }}>Pronunciation practice</Button>
                <Box>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 17, color: NAVY, mb: 1.5 }}>
                        Your courses
                    </Typography>
                    <Stack spacing={1.5}>
                        {mockCourses.map(course => (
                            <CourseCard key={course.id} course={course} />
                        ))}
                    </Stack>
                </Box>

            </Stack>
        </PhoneFrame>
    );
}
